import { useEffect, useState } from 'react';
import { Loader2, Plus, X, Check } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { LoginArea } from '@/components/auth/LoginArea';
import { BackButton } from '@/components/stashstr/BackButton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { usePayto } from '@/hooks/usePayto';
import {
  PAYTO_TYPES,
  isValidPaytoAddress,
  isValidPaytoType,
  paytoLabel,
  truncateAddr,
} from '@/lib/payto';

interface Row {
  type: string;
  address: string;
}

export default function PaymentsSettings() {
  const { user } = useCurrentUser();
  const { data: existing, isLoading } = usePayto(user?.pubkey);
  const publish = useNostrPublish();

  const [rows, setRows] = useState<Row[]>([{ type: 'lightning', address: '' }]);
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Prefill from existing kind 10133
  useEffect(() => {
    if (existing && !loaded) {
      setRows(
        existing.length > 0
          ? existing.map((e) => ({ ...e }))
          : [{ type: 'lightning', address: '' }],
      );
      setLoaded(true);
    }
  }, [existing, loaded]);

  if (!user) {
    return (
      <div>
        <BackButton />
        <h1 className="text-[18px] font-bold text-gray-900 dark:text-gray-100 mb-3">
          payment addresses
        </h1>
        <Card className="border-dashed">
          <CardContent className="py-12 text-center flex flex-col items-center gap-3">
            <p className="text-muted-foreground text-[14px]">
              Log in to manage your tipping addresses (NIP-A3 payto).
            </p>
            <LoginArea className="max-w-60" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-gray-500 dark:text-gray-400 py-8 justify-center">
        <Loader2 className="size-4 animate-spin" /> Loading your addresses…
      </div>
    );
  }

  const setRow = (i: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setSaved(false);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { type: 'bitcoin', address: '' }]);
    setSaved(false);
  };

  const removeRow = (i: number) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : prev));
    setSaved(false);
  };

  const save = async () => {
    setError(undefined);
    const cleaned = rows
      .map((r) => ({ type: r.type.trim().toLowerCase(), address: r.address.trim() }))
      .filter((r) => r.address.length > 0);

    if (cleaned.length === 0) {
      setError('Add at least one address.');
      return;
    }
    for (const r of cleaned) {
      if (!isValidPaytoType(r.type)) {
        setError(`Invalid coin type "${r.type}" (lowercase letters, numbers, dashes only).`);
        return;
      }
      if (!isValidPaytoAddress(r.address)) {
        setError(`Invalid address for ${paytoLabel(r.type)}.`);
        return;
      }
    }

    try {
      await publish.mutateAsync({
        kind: 10133,
        content: '',
        tags: cleaned.map((r) => ['payto', r.type, r.address]),
      });
      setSaved(true);
    } catch {
      setError('Failed to save. Try again.');
    }
  };

  return (
    <div>
      <BackButton />
      <h1 className="text-[18px] font-bold text-gray-900 dark:text-gray-100 mb-1">
        payment addresses
      </h1>
      <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-4">
        Published as NIP-A3 payto targets (kind 10133). Lightning addresses enable countable
        zaps. Other coins are private tips shown as copy/QR.
      </p>

      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2 items-center">
            <select
              value={PAYTO_TYPES.includes(row.type as never) ? row.type : '__custom'}
              onChange={(e) => {
                if (e.target.value === '__custom') {
                  setRow(i, { type: '' });
                } else {
                  setRow(i, { type: e.target.value });
                }
              }}
              className="flex h-9 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-[13px]"
              aria-label={`Coin type ${i + 1}`}
            >
              {PAYTO_TYPES.map((t) => (
                <option key={t} value={t}>
                  {paytoLabel(t)}
                </option>
              ))}
              <option value="__custom">Custom…</option>
            </select>

            {!PAYTO_TYPES.includes(row.type as never) && (
              <Input
                value={row.type}
                onChange={(e) => setRow(i, { type: e.target.value.toLowerCase() })}
                placeholder="coin-type"
                className="text-[13px] w-28"
                aria-label={`Custom coin type ${i + 1}`}
              />
            )}

            <Input
              value={row.address}
              onChange={(e) => setRow(i, { address: e.target.value })}
              placeholder={
                row.type === 'lightning' ? 'you@wallet.com' : `${row.type || 'coin'} address`
              }
              className="text-[13px] flex-1 font-mono"
              aria-label={`Address ${i + 1}`}
            />

            {rows.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeRow(i)}
                aria-label={`Remove ${paytoLabel(row.type)} address`}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          className="text-[12px]"
        >
          <Plus className="size-3.5 mr-1" /> Add address
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={publish.isPending}
          className="h-8 px-4 text-[13px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
        >
          {publish.isPending ? <Loader2 className="size-4 animate-spin mr-1" /> : null}
          Save
        </Button>
        {saved && (
          <span className="text-[12px] text-green-600 dark:text-green-400 inline-flex items-center gap-1">
            <Check className="size-3.5" /> Saved!
          </span>
        )}
      </div>

      {error && <p className="text-[12px] text-red-600 dark:text-red-400 mt-2">{error}</p>}

      {existing && existing.length > 0 && (
        <div className="mt-6 text-[12px] text-gray-500 dark:text-gray-400">
          <p className="font-medium mb-1">Currently published:</p>
          <ul className="list-disc list-inside space-y-0.5">
            {existing.map((e, i) => (
              <li key={i}>
                {paytoLabel(e.type)} · <span className="font-mono">{truncateAddr(e.address)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
